import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import { analytics, chord, identify as _identify, reset } from './utils';
import { cartViewed as chordCartViewed, checkoutCompleted as chordCheckoutCompleted, checkoutStarted as chordCheckoutStarted, collectionClicked as chordCollectionClicked, couponApplied as chordCouponApplied, couponDenied as chordCouponDenied, couponEntered as chordCouponEntered, couponRemoved as chordCouponRemoved, emailCaptured as chordEmailCaptured, loginStarted as chordLoginStarted, orderCompleted as chordOrderCompleted, productAdded as chordProductAdded, productClicked as chordProductClicked, productRemoved as chordProductRemoved, productViewed as chordProductViewed, signedIn as chordSignedIn, signedOut as chordSignedOut, stockRequestCreated as chordStockRequestCreated, subscriptionCancelled as chordSubscriptionCancelled, subscriptionOrderSkipped as chordSubscriptionOrderSkipped, subscriptionPaused as chordSubscriptionPaused, subscriptionResumed as chordSubscriptionResumed, variantClicked as chordVariantClicked } from './chord';
import { cartViewed, checkoutCompleted, checkoutStarted, collectionClicked, couponApplied, couponDenied, couponEntered, couponRemoved, emailCaptured, loginStarted, orderCompleted, productAdded, productClicked, productRemoved, productViewed, signedIn, signedOut, stockRequestCreated, subscriptionCancelled, subscriptionOrderSkipped, subscriptionPaused, subscriptionResumed, variantClicked } from './segment/typewriter';
import { serializeCartViewed, serializeCheckoutCompleted, serializeCheckoutStarted, serializeCollectionClicked, serializeCouponItem, serializeEmailCaptured, serializeMeta, serializeOrderCompleted, serializeProductAdded, serializeProductClicked, serializeProductRemoved, serializeProductViewed, serializeStockRequestCreated, serializeSubscription, serializeVariantClicked, serializeUnpurchasedSubscription, serializePrepaidSubscription } from './serialize';
import { createAnalyticsConfig } from './config';
var ANALYTICS_TIMEOUT = 2500;
/**
 * Client for interacting with Segment Analytics and/or Chord CDP.
 *
 * The analytics.js snippet should be available via window.analytics.
 * You can install it by following instructions at: https://segment.com/docs/sources/website/analytics.js/quickstart/
 *
 * The Chord CDP snippet should be available via window.chord.
 */
export var createAnalyticsClient = function createAnalyticsClient(analyticsClientConfig) {
  var config = createAnalyticsConfig(analyticsClientConfig);
  var meta = serializeMeta(config);
  if (typeof window !== 'undefined') {
    setTimeout(function () {
      if (!analytics() && !chord()) {
        var message = 'window.analytics and window.chord are undefined. Neither Segment or Chord CDP are installed correctly, so Chord cannot send events to a CDP. See https://docs.chord.co/adding-tracking for troubleshooting.';
        // eslint-disable-next-line no-console
        console[process.env.NODE_ENV !== 'production' ? 'error' : 'warn'](message);
      }
    }, ANALYTICS_TIMEOUT);
  }
  return {
    identify: function identify(userId, traits, options, callback) {
      _identify(userId, _objectSpread(_objectSpread({}, traits), meta), options, callback);
    },
    page: function page(url) {
      var _analytics, _chord;
      var properties = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      (_analytics = analytics()) === null || _analytics === void 0 ? void 0 : _analytics.page(url, _objectSpread(_objectSpread({}, meta), properties));
      (_chord = chord()) === null || _chord === void 0 ? void 0 : _chord.page(url, _objectSpread(_objectSpread({}, meta), properties));
    },
    trackCartViewed: function trackCartViewed(props) {
      var serialized = serializeCartViewed(config, props);
      for (var _len = arguments.length, rest = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        rest[_key - 1] = arguments[_key];
      }
      cartViewed.apply(void 0, [serialized].concat(rest));
      chordCartViewed.apply(void 0, [serialized].concat(rest));
    },
    trackCheckoutCompleted: function trackCheckoutCompleted(props) {
      var _props$order, _props$order$userId, _props$order2;
      var userId = ((_props$order = props.order) === null || _props$order === void 0 ? void 0 : (_props$order$userId = _props$order.userId) === null || _props$order$userId === void 0 ? void 0 : _props$order$userId.toString()) || '';
      var email = (_props$order2 = props.order) === null || _props$order2 === void 0 ? void 0 : _props$order2.email;
      _identify(userId, {
        email: email
      });
      var serialized = serializeCheckoutCompleted(config, props);
      for (var _len2 = arguments.length, rest = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        rest[_key2 - 1] = arguments[_key2];
      }
      checkoutCompleted.apply(void 0, [serialized].concat(rest));
      chordCheckoutCompleted.apply(void 0, [serialized].concat(rest));
    },
    trackCheckoutStarted: function trackCheckoutStarted(props) {
      var serialized = serializeCheckoutStarted(config, props);
      for (var _len3 = arguments.length, rest = new Array(_len3 > 1 ? _len3 - 1 : 0), _key3 = 1; _key3 < _len3; _key3++) {
        rest[_key3 - 1] = arguments[_key3];
      }
      checkoutStarted.apply(void 0, [serialized].concat(rest));
      chordCheckoutStarted.apply(void 0, [serialized].concat(rest));
    },
    trackCollectionClicked: function trackCollectionClicked(props) {
      var serialized = serializeCollectionClicked(config, props);
      for (var _len4 = arguments.length, rest = new Array(_len4 > 1 ? _len4 - 1 : 0), _key4 = 1; _key4 < _len4; _key4++) {
        rest[_key4 - 1] = arguments[_key4];
      }
      collectionClicked.apply(void 0, [serialized].concat(rest));
      chordCollectionClicked.apply(void 0, [serialized].concat(rest));
    },
    trackCouponApplied: function trackCouponApplied(props) {
      var serialized = serializeCouponItem(config, props);
      for (var _len5 = arguments.length, rest = new Array(_len5 > 1 ? _len5 - 1 : 0), _key5 = 1; _key5 < _len5; _key5++) {
        rest[_key5 - 1] = arguments[_key5];
      }
      couponApplied.apply(void 0, [serialized].concat(rest));
      chordCouponApplied.apply(void 0, [serialized].concat(rest));
    },
    trackCouponDenied: function trackCouponDenied(props) {
      var serialized = _objectSpread(_objectSpread({}, serializeCouponItem(config, props)), {}, {
        reason: props.reason
      });
      for (var _len6 = arguments.length, rest = new Array(_len6 > 1 ? _len6 - 1 : 0), _key6 = 1; _key6 < _len6; _key6++) {
        rest[_key6 - 1] = arguments[_key6];
      }
      couponDenied.apply(void 0, [serialized].concat(rest));
      chordCouponDenied.apply(void 0, [serialized].concat(rest));
    },
    trackCouponEntered: function trackCouponEntered(props) {
      var serialized = serializeCouponItem(config, props);
      for (var _len7 = arguments.length, rest = new Array(_len7 > 1 ? _len7 - 1 : 0), _key7 = 1; _key7 < _len7; _key7++) {
        rest[_key7 - 1] = arguments[_key7];
      }
      couponEntered.apply(void 0, [serialized].concat(rest));
      chordCouponEntered.apply(void 0, [serialized].concat(rest));
    },
    trackCouponRemoved: function trackCouponRemoved(props) {
      var serialized = serializeCouponItem(config, props);
      for (var _len8 = arguments.length, rest = new Array(_len8 > 1 ? _len8 - 1 : 0), _key8 = 1; _key8 < _len8; _key8++) {
        rest[_key8 - 1] = arguments[_key8];
      }
      couponRemoved.apply(void 0, [serialized].concat(rest));
      chordCouponRemoved.apply(void 0, [serialized].concat(rest));
    },
    trackEmailCaptured: function trackEmailCaptured(props) {
      _identify('', {
        email: props.emailCapture.email
      });
      var serialized = serializeEmailCaptured(config, props);
      for (var _len9 = arguments.length, rest = new Array(_len9 > 1 ? _len9 - 1 : 0), _key9 = 1; _key9 < _len9; _key9++) {
        rest[_key9 - 1] = arguments[_key9];
      }
      emailCaptured.apply(void 0, [serialized].concat(rest));
      chordEmailCaptured.apply(void 0, [serialized].concat(rest));
    },
    trackLoginStarted: function trackLoginStarted(props) {
      if (props !== null && props !== void 0 && props.email) {
        _identify('', {
          email: props.email
        });
      }
      for (var _len10 = arguments.length, rest = new Array(_len10 > 1 ? _len10 - 1 : 0), _key10 = 1; _key10 < _len10; _key10++) {
        rest[_key10 - 1] = arguments[_key10];
      }
      loginStarted.apply(void 0, [_objectSpread(_objectSpread({}, props), meta)].concat(rest));
      chordLoginStarted.apply(void 0, [_objectSpread(_objectSpread({}, props), meta)].concat(rest));
    },
    trackOrderCompleted: function trackOrderCompleted(props) {
      var serialized = serializeOrderCompleted(config, props);
      for (var _len11 = arguments.length, rest = new Array(_len11 > 1 ? _len11 - 1 : 0), _key11 = 1; _key11 < _len11; _key11++) {
        rest[_key11 - 1] = arguments[_key11];
      }
      orderCompleted.apply(void 0, [serialized].concat(rest));
      chordOrderCompleted.apply(void 0, [serialized].concat(rest));
    },
    trackProductAdded: function trackProductAdded(props) {
      var serialized = serializeProductAdded(config, props);
      for (var _len12 = arguments.length, rest = new Array(_len12 > 1 ? _len12 - 1 : 0), _key12 = 1; _key12 < _len12; _key12++) {
        rest[_key12 - 1] = arguments[_key12];
      }
      productAdded.apply(void 0, [serialized].concat(rest));
      chordProductAdded.apply(void 0, [serialized].concat(rest));
    },
    trackProductClicked: function trackProductClicked(props) {
      var serialized = serializeProductClicked(config, props);
      for (var _len13 = arguments.length, rest = new Array(_len13 > 1 ? _len13 - 1 : 0), _key13 = 1; _key13 < _len13; _key13++) {
        rest[_key13 - 1] = arguments[_key13];
      }
      productClicked.apply(void 0, [serialized].concat(rest));
      chordProductClicked.apply(void 0, [serialized].concat(rest));
    },
    trackProductRemoved: function trackProductRemoved(props) {
      var serialized = serializeProductRemoved(config, props);
      for (var _len14 = arguments.length, rest = new Array(_len14 > 1 ? _len14 - 1 : 0), _key14 = 1; _key14 < _len14; _key14++) {
        rest[_key14 - 1] = arguments[_key14];
      }
      productRemoved.apply(void 0, [serialized].concat(rest));
      chordProductRemoved.apply(void 0, [serialized].concat(rest));
    },
    trackProductViewed: function trackProductViewed(props) {
      var serialized = serializeProductViewed(config, props);
      for (var _len15 = arguments.length, rest = new Array(_len15 > 1 ? _len15 - 1 : 0), _key15 = 1; _key15 < _len15; _key15++) {
        rest[_key15 - 1] = arguments[_key15];
      }
      productViewed.apply(void 0, [serialized].concat(rest));
      chordProductViewed.apply(void 0, [serialized].concat(rest));
    },
    trackSignedIn: function trackSignedIn(props) {
      _identify(props.userId, {
        email: props.email
      });
      for (var _len16 = arguments.length, rest = new Array(_len16 > 1 ? _len16 - 1 : 0), _key16 = 1; _key16 < _len16; _key16++) {
        rest[_key16 - 1] = arguments[_key16];
      }
      signedIn.apply(void 0, [_objectSpread(_objectSpread({}, props), meta)].concat(rest));
      chordSignedIn.apply(void 0, [_objectSpread(_objectSpread({}, props), meta)].concat(rest));
    },
    trackSignedOut: function trackSignedOut(props, options, callback) {
      signedOut(_objectSpread(_objectSpread({}, props), meta), options, function () {
        reset();
        if (callback && typeof callback === 'function') {
          callback();
        }
      });
      chordSignedOut(_objectSpread(_objectSpread({}, props), meta), options, function () {
        reset();
        if (callback && typeof callback === 'function') {
          callback();
        }
      });
    },
    trackStockRequestCreated: function trackStockRequestCreated(props) {
      _identify('', {
        email: props.email
      });
      var serialized = serializeStockRequestCreated(config, props);
      for (var _len17 = arguments.length, rest = new Array(_len17 > 1 ? _len17 - 1 : 0), _key17 = 1; _key17 < _len17; _key17++) {
        rest[_key17 - 1] = arguments[_key17];
      }
      stockRequestCreated.apply(void 0, [serialized].concat(rest));
      chordStockRequestCreated.apply(void 0, [serialized].concat(rest));
    },
    trackSubscriptionAdded: function trackSubscriptionAdded(props) {
      var serialized = serializeUnpurchasedSubscription(config, props);
      for (var _len18 = arguments.length, rest = new Array(_len18 > 1 ? _len18 - 1 : 0), _key18 = 1; _key18 < _len18; _key18++) {
        rest[_key18 - 1] = arguments[_key18];
      }
      productAdded.apply(void 0, [serialized].concat(rest));
      chordProductAdded.apply(void 0, [serialized].concat(rest));
    },
    trackPrepaidSubscriptionAdded: function trackPrepaidSubscriptionAdded(props) {
      var serialized = serializePrepaidSubscription(config, props);
      for (var _len19 = arguments.length, rest = new Array(_len19 > 1 ? _len19 - 1 : 0), _key19 = 1; _key19 < _len19; _key19++) {
        rest[_key19 - 1] = arguments[_key19];
      }
      productAdded.apply(void 0, [serialized].concat(rest));
      chordProductAdded.apply(void 0, [serialized].concat(rest));
    },
    trackSubscriptionCancelled: function trackSubscriptionCancelled(props) {
      var serialized = serializeSubscription(config, props);
      for (var _len20 = arguments.length, rest = new Array(_len20 > 1 ? _len20 - 1 : 0), _key20 = 1; _key20 < _len20; _key20++) {
        rest[_key20 - 1] = arguments[_key20];
      }
      subscriptionCancelled.apply(void 0, [serialized].concat(rest));
      chordSubscriptionCancelled.apply(void 0, [serialized].concat(rest));
    },
    trackSubscriptionPaused: function trackSubscriptionPaused(props) {
      var serialized = serializeSubscription(config, props);
      for (var _len21 = arguments.length, rest = new Array(_len21 > 1 ? _len21 - 1 : 0), _key21 = 1; _key21 < _len21; _key21++) {
        rest[_key21 - 1] = arguments[_key21];
      }
      subscriptionPaused.apply(void 0, [serialized].concat(rest));
      chordSubscriptionPaused.apply(void 0, [serialized].concat(rest));
    },
    trackSubscriptionResumed: function trackSubscriptionResumed(props) {
      var serialized = serializeSubscription(config, props);
      for (var _len22 = arguments.length, rest = new Array(_len22 > 1 ? _len22 - 1 : 0), _key22 = 1; _key22 < _len22; _key22++) {
        rest[_key22 - 1] = arguments[_key22];
      }
      subscriptionResumed.apply(void 0, [serialized].concat(rest));
      chordSubscriptionResumed.apply(void 0, [serialized].concat(rest));
    },
    trackSubscriptionSkipped: function trackSubscriptionSkipped(props) {
      var serialized = serializeSubscription(config, props);
      for (var _len23 = arguments.length, rest = new Array(_len23 > 1 ? _len23 - 1 : 0), _key23 = 1; _key23 < _len23; _key23++) {
        rest[_key23 - 1] = arguments[_key23];
      }
      subscriptionOrderSkipped.apply(void 0, [serialized].concat(rest));
      chordSubscriptionOrderSkipped.apply(void 0, [serialized].concat(rest));
    },
    trackVariantClicked: function trackVariantClicked(props) {
      var serialized = serializeVariantClicked(config, props);
      for (var _len24 = arguments.length, rest = new Array(_len24 > 1 ? _len24 - 1 : 0), _key24 = 1; _key24 < _len24; _key24++) {
        rest[_key24 - 1] = arguments[_key24];
      }
      variantClicked.apply(void 0, [serialized].concat(rest));
      chordVariantClicked.apply(void 0, [serialized].concat(rest));
    }
  };
};